import { Pencil } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { InfoTip } from './InfoTip'
import { Tooltip } from './Tooltip'
import './answer-line.css'

/**
 * A question already answered, folded to one line with the way to change it.
 *
 * The line is what a one-question-at-a-time flow leaves behind, so it has to
 * carry the answer at every width. That is why it is not an `InfoStrip` cell:
 * a strip GIVES WAY on a phone, on the reasoning that every fact it states is
 * one tap away in the panel it summarizes. Here there is no panel — the answer
 * exists only because somebody just gave it, and dropping the line at 375px
 * would erase what they chose on the surface where most gifts are made.
 *
 * So it folds instead: the answer moves under its question rather than beside
 * it, and Edit stays where it was.
 *
 * Where the flow IS the page — a public payment — the answer is what the page
 * reads back, so it is set in the serif at a reading size against a column of
 * questions. Inside a form it is one line at the form's own size.
 */
export function AnswerLine({
  label,
  value,
  detail,
  info,
  changeLabel,
  onChange,
  changeDisabled = false,
  appearance = 'form',
  figure = false,
  words = false,
  verb,
}: {
  /** The question this answers. */
  label: string
  /** What was chosen, as the donor reads it back. */
  value: ReactNode
  /** The donor's own words, or a second fact the answer carries. */
  detail?: ReactNode
  /**
   * What is true of the answer without being part of it — who last changed it,
   * and when. Behind an ⓘ beside the answer, so the line stays the answer.
   */
  info?: ReactNode
  /**
   * What the control says, which names the question rather than the act: a
   * column of rows all saying "Change" says nothing about which one you are
   * about to reopen. Absent when the answer cannot be changed.
   */
  changeLabel?: string
  onChange?: () => void
  changeDisabled?: boolean
  appearance?: 'form' | 'page'
  /** The answer is a sum, and is set in the figure face like every sum. */
  figure?: boolean
  /** The answer is somebody's own sentences: it takes the direction of what
   *  they wrote, and on a page it stops at three lines. */
  words?: boolean
  /**
   * The word on the control where it performs an act rather than reopening a
   * question — "Send". A line whose control does something other than edit
   * must not wear the edit mark, or nobody reads it as the way to do it.
   */
  verb?: string
}) {
  return (
    <div
      className="answer-line"
      data-appearance={appearance === 'page' ? 'page' : undefined}
      data-words={words ? '' : undefined}
    >
      <div className="answer-line__text">
        <span className="answer-line__label">{label}</span>
        <span
          className="answer-line__value"
          data-figure={figure ? '' : undefined}
          dir={words ? 'auto' : undefined}
        >
          {value}
        </span>
        {info ? (
          <InfoTip size="inline" label={`About ${label}`}>
            {info}
          </InfoTip>
        ) : null}
        {detail ? <span className="answer-line__detail">{detail}</span> : null}
      </div>
      {/* The accessible name says which question it reopens, so a reader
          hearing the row knows without the column. On a page the edit pencil
          stands alone: it sits beside every answer, so the word would be one
          more line of type repeated down the column. A line that performs an
          act says its verb wherever it is. */}
      {onChange && changeLabel ? (
        appearance === 'page' && !verb ? (
          <Tooltip label="Edit">
            <Button
              variant="quiet"
              icon
              className="answer-line__change"
              aria-label={changeLabel}
              onClick={onChange}
              disabled={changeDisabled}
            >
              <Pencil className="btn__icon" aria-hidden />
            </Button>
          </Tooltip>
        ) : (
          <Button
            variant={verb ? 'secondary' : 'quiet'}
            className="answer-line__change"
            aria-label={changeLabel}
            onClick={onChange}
            disabled={changeDisabled}
          >
            {verb ? null : <Pencil className="btn__icon" aria-hidden />}
            <span aria-hidden>{verb ?? 'Edit'}</span>
          </Button>
        )
      ) : null}
    </div>
  )
}
